import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  documentCaseColumns,
  documentQualityColumns,
} from '../../../config/agentic/documentQualityConfig';

import {
  mapDocumentRows,
} from '../../../utils/helper';

import DocumentViewerModal from '../DocumentViewerModal';
import DuplicateSection from '../DuplicateSection';
import ReportLegend from '../ReportLegend';
import ReportSummary from '../ReportSummary';

const DocumentQualityReport = React.memo(({ reportData }) => {
  const [openDoc, setOpenDoc] = useState(null);

  const [docsNeedingCorrection, setDocsNeedingCorrection] = useState([]);
  const [acceptableDocs, setAcceptableDocs] = useState([]);

  useEffect(() => {
    if (!reportData) {
      setDocsNeedingCorrection([]);
      setAcceptableDocs([]);
      return;
    }

    if (!reportData?.documentRows?.length) {
      setDocsNeedingCorrection([]);
      setAcceptableDocs([]);
      return;
    }

    const mapped = mapDocumentRows(reportData.documentRows);

    setDocsNeedingCorrection(
      mapped.filter(
        (row) => !row.notes?.startsWith('Acceptable'),
      ),
    );

    setAcceptableDocs(
      mapped.filter(
        (row) => row.notes?.startsWith('Acceptable'),
      ),
    );
  }, [reportData]);

  if (!reportData) return null;

  const handleMarkDocAcceptable = (docs) => {
    const ids = new Set(
      docs.map((doc) => doc.documentId),
    );

    setDocsNeedingCorrection((prev) =>
      prev.filter((row) => !ids.has(row.documentId)),
    );

    setAcceptableDocs((prev) => {
      const existingIds = new Set(
        prev.map((row) => row.documentId),
      );

      const newDocs = docs.filter(
        (doc) => !existingIds.has(doc.documentId),
      );

      return [...prev, ...newDocs];
    });
  };

  const handleMarkDocNotAcceptable = (docs) => {
    const ids = new Set(
      docs.map((doc) => doc.documentId),
    );

    setAcceptableDocs((prev) =>
      prev.filter((row) => !ids.has(row.documentId)),
    );

    setDocsNeedingCorrection((prev) => {
      const existingIds = new Set(
        prev.map((row) => row.documentId),
      );

      const newDocs = docs.filter(
        (doc) => !existingIds.has(doc.documentId),
      );

      return [...prev, ...newDocs];
    });
  };

  return (
    <div
      style={{
        paddingBottom:  '8rem',
      }}
    >
      <ReportLegend
        title={`Document Quality Report: Application ${
          reportData?.caseRow?.caseId ?? 'N/A'
        }`}
        legends={false}
      />

      <ReportSummary summary={reportData.summary} />

      <DuplicateSection
        title="Application Details"
        columns={documentCaseColumns}
        data={reportData.caseRow ? [reportData.caseRow] : []}
        showCheckbox={false}
        tableClassName="defaultStickyTableDQA"
      />

      <DuplicateSection
        title="Acceptable Documents"
        columns={documentQualityColumns(setOpenDoc)}
        data={acceptableDocs}
        onMarkDuplicate={handleMarkDocNotAcceptable}
        showActions
        actionLabel="Mark as not Acceptable"
        tableClassName="defaultStickyTableDQA"
      />

      <DuplicateSection
        title="Documents Requiring Correction"
        columns={documentQualityColumns(setOpenDoc)}
        data={docsNeedingCorrection}
        onMarkDuplicate={handleMarkDocAcceptable}
        showActions
        actionLabel="Mark as Acceptable"
        tableClassName="defaultStickyTableDQA"
      />

      <DocumentViewerModal
        fileUrl={openDoc?.fileUrl}
        documentName={openDoc?.documentName}
        onClose={() => setOpenDoc(null)}
      />
    </div>
  );
});

DocumentQualityReport.propTypes = {
  reportData: PropTypes.shape({
    summary: PropTypes.string,
    caseRow: PropTypes.object,
    documentRows: PropTypes.array,
  }),
};

DocumentQualityReport.defaultProps = {
  reportData: null,
};

export default DocumentQualityReport;